import { useContext, useEffect, useState } from 'react'
import { View } from 'react-native'
import { useMutation, useQuery } from '@apollo/client'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'

import { BuyOrderStatus } from '@/constants/common'
import { CANCEL_BUY_ORDER } from '@/constants/graphql/mutations'
import { USER_GET_BUY_ORDER_BY_INTERNAL_KEY } from '@/constants/graphql/queries'
import type { BuyQueryResult } from '@/types/common'
import { delay } from '@/utils/delay'
import { getGraphQlErrorCode } from '@/utils/graphqlError'
import { useModal, useToast } from '@/components/base'
import { Spinner } from '@/components/icon'
import { Stack } from '@/components/layout'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import NotFoundScreen from '@/components/NotFoundScreen'
import ServerErrorScreen from '@/components/ServerErrorScreen'
import { AppealRequest } from '@/components/orders/AppealRequest'
import { BuyerMatchingScreen } from '@/components/orders/BuyerMatchingScreen'
import CancelModal from '@/components/orders/CancelModal'
import ChildOrderAppealingScreen from '@/components/orders/child/AppealingScreen'
import { ChildOrderCanceledScreen } from '@/components/orders/child/CanceledScreen'
import { ChildOrderCancelingScreen } from '@/components/orders/child/CancelingScreen'
import { ChildOrderCompletedScreen } from '@/components/orders/child/CompletedScreen'
import { ChildOrderRevisingScreen } from '@/components/orders/child/RevisingScreen'
import { ChildOrderWaitingForCoinReleaseScreen } from '@/components/orders/child/WaitingForCoinRelease'
import { ChildOrderWaitingForPaymentScreen } from '@/components/orders/child/WaitingForPaymentScreen'
import OrderHeader from '@/components/orders/OrderHeader'
import { CustomerServiceContext } from '@/components/providers/CustomerService'

export default function BuyOrderDetailsScreen() {
  const { t } = useTranslation()
  const id = useLocalSearchParams().id
  const { openModal, closeModal } = useModal()
  const loading = useLoadingIndicator()
  const [requestingAppeal, setRequestingAppeal] = useState<boolean>(false)
  const { openChat } = useContext(CustomerServiceContext)
  const toast = useToast()

  const {
    data,
    error,
    loading: isLoading,
    refetch
  } = useQuery<BuyQueryResult>(USER_GET_BUY_ORDER_BY_INTERNAL_KEY, {
    variables: { internalKey: id }
  })

  const buyOrder = data?.userGetBuyOrderByInternalKey

  useEffect(() => {
    return () => {
      loading.stop()
    }
  }, [loading.stop])

  const [cancelBuyOrder] = useMutation(CANCEL_BUY_ORDER, {
    onCompleted: () => {
      delay(1000).then(() => {
        refetch()
        loading.stop()
      })
    },
    onError: (error) => {
      console.error('cancelBuyOrder error:', error)
      refetch()
      loading.stop()
      toast.show({ variant: 'error', description: t('auth:toast.error.invalidOperationHint') })
    }
  })

  const handleBuyerCancel = (buyOrderId: string) => {
    // console.log({ buyOrderId })
    loading.start()
    cancelBuyOrder({
      variables: {
        buyOrderId
      }
    })
  }

  function handleOpenAppealModal() {
    setRequestingAppeal(true)
  }

  function handleCustomerService() {
    openChat()
  }

  const completeAppeal = () => {
    refetch()
    setRequestingAppeal(false)
  }

  if (!buyOrder && isLoading) {
    return (
      <>
        <Stack.Screen options={{ header: () => <OrderHeader /> }} />
        <View className={'h-full items-center justify-center bg-background-secondary'}>
          <View className={'animate-spin'}>
            <Spinner className={'size-6 text-primary'} />
          </View>
        </View>
      </>
    )
  }

  if (!buyOrder && error && getGraphQlErrorCode(error) !== 2000) {
    return <ServerErrorScreen onPress={() => void refetch()} />
  }

  if (!buyOrder) {
    return <NotFoundScreen />
  }

  if (requestingAppeal) {
    return (
      <AppealRequest
        buyOrder={buyOrder}
        isBuyer={true}
        cancelAppeal={() => {
          setRequestingAppeal(false)
        }}
        completeAppeal={completeAppeal}
      />
    )
  }

  const status = buyOrder.status
  const canGetHelp = status === BuyOrderStatus.appealing
  const canCancel = status === BuyOrderStatus.paired

  if (status === BuyOrderStatus.completed) {
    return (
      <>
        <Stack.Screen options={{ header: () => <OrderHeader /> }} />
        <ChildOrderCompletedScreen
          data={buyOrder}
          type={'buy'}
          parentInternalKey={buyOrder.internalKey}
          parentId={buyOrder.id}
        />
      </>
    )
  }

  let Component
  switch (status) {
    case BuyOrderStatus.unpaired:
      Component = BuyerMatchingScreen
      break
    case BuyOrderStatus.paired:
      Component = ChildOrderWaitingForPaymentScreen
      break
    case BuyOrderStatus.paymentMade:
      Component = ChildOrderWaitingForCoinReleaseScreen
      break
    case BuyOrderStatus.canceling:
      Component = ChildOrderCancelingScreen
      break
    case BuyOrderStatus.canceled:
    case BuyOrderStatus.forceCanceled:
      Component = ChildOrderCanceledScreen
      break
    case BuyOrderStatus.complainted:
      Component = ChildOrderRevisingScreen
      break
    case BuyOrderStatus.appealing:
      Component = ChildOrderAppealingScreen
      break
    default:
      throw new Error(`unexpected order status: ${status}`)
  }

  return (
    <>
      <Stack.Screen
        options={{
          header: () => (
            <OrderHeader
              text={canCancel ? t('orders:cancelOrder') : canGetHelp ? t('support:contactCustomerService') : ''}
              onPress={
                canCancel
                  ? () => {
                      openModal({
                        children: <CancelModal onSubmit={() => handleBuyerCancel(buyOrder.id)} />,
                        onClose: closeModal
                      })
                    }
                  : canGetHelp
                    ? handleCustomerService
                    : undefined
              }
            />
          )
        }}
      />
      <Component data={buyOrder} refetch={refetch} type={'buy'} requestAppeal={handleOpenAppealModal} />
    </>
  )
}
